/**
 * A custom element implementing the WAI-ARIA Tabs Pattern with automatic
 * activation. Tabs and panels are server-rendered by the `tabs` and `_tab`
 * blocks; this element wires up selection state and keyboard navigation.
 */
class TabsComponent extends HTMLElement {
  #controller = new AbortController();

  get #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]')).filter(
      (tab) => tab.closest('tabs-component') === this
    );
  }

  get #panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]')).filter(
      (panel) => panel.closest('tabs-component') === this
    );
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.classList.add('tabs--js');
    this.#labelTabs();

    const selected =
      this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabs[0];
    if (selected) this.#select(selected);

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Copies each panel's data-tab-title onto its tab button. Titles live on
   * the panels because Liquid can't read child block settings from the
   * parent's block.blocks loop.
   */
  #labelTabs() {
    for (const tab of this.#tabs) {
      const panel = this.#panels.find((candidate) => candidate.id === tab.getAttribute('aria-controls'));
      if (panel?.dataset.tabTitle) tab.textContent = panel.dataset.tabTitle;
    }
  }

  /**
   * Activates the tab that was clicked.
   * @param {Event} event - The event.
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (tab && this.#tabs.includes(tab)) this.#select(tab);
  };

  /**
   * Moves selection with Arrow/Home/End keys, per the ARIA tabs pattern.
   * @param {KeyboardEvent} event - The event.
   */
  #handleKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    const tabs = this.#tabs;
    const index = tabs.indexOf(tab);
    if (index === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next, { focus: true });
  };

  /**
   * Reveals a tab panel when its block is selected in the theme editor.
   * @param {Event} event - The event.
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const panel = this.#panels.find((candidate) => candidate.contains(event.target));
    if (!panel) return;

    const tab = this.#tabs.find((candidate) => candidate.id === panel.getAttribute('aria-labelledby'));
    if (tab) this.#select(tab);
  };

  /**
   * Selects a tab: updates selection state and shows its panel.
   * @param {Element} tab - The tab to select.
   * @param {{ focus?: boolean }} [options] - Whether to move focus to the tab.
   */
  #select(tab, { focus = false } = {}) {
    for (const other of this.#tabs) {
      const active = other === tab;
      other.setAttribute('aria-selected', active ? 'true' : 'false');
      other.setAttribute('tabindex', active ? '0' : '-1');
    }

    for (const panel of this.#panels) {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
    }

    if (focus && tab instanceof HTMLElement) tab.focus();
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
